import { AgentResponse, ConversationMessage, QuickAction } from './types';
import { agentPersonality } from './agentPersonality';
import { conversationContext } from './conversationContext';
import { cachedResponses } from './cachedResponses';
import { conversationActions } from './conversationActions';
import { messageQueue } from './messageQueue';
import { agentAnalytics } from './agentAnalytics';
import { edgeFunctions } from '@/services/edgeFunctions';

export class AgriAgentService {
  private conversationHistory: ConversationMessage[] = [];
  private isOnline: boolean = navigator.onLine;
  private userId: string | null = null;

  constructor() {
    this.loadHistory();
    this.setupNetworkListeners();
    agentAnalytics.startSession();
  }

  setUserId(userId: string): void {
    this.userId = userId;
  }

  private setupNetworkListeners(): void {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processQueuedMessages();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  private loadHistory(): void {
    const stored = localStorage.getItem('agriagent_history');
    if (stored) {
      const parsed = JSON.parse(stored);
      this.conversationHistory = parsed.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));
    }
  }

  private saveHistory(): void {
    // Keep only last 50 messages
    const recentHistory = this.conversationHistory.slice(-50);
    localStorage.setItem('agriagent_history', JSON.stringify(recentHistory));
  }

  async sendMessage(text: string, language: string = 'en'): Promise<AgentResponse> {
    agentAnalytics.trackMessage(true, text);
    conversationContext.addQuestion(text);

    // If offline, use cached/queued responses
    if (!this.isOnline) {
      const queueId = messageQueue.addToQueue(text);
      const cachedResponse = cachedResponses.getResponse(text);
      if (cachedResponse) {
        return { ...cachedResponse, content: `[Offline] ${cachedResponse.content}` };
      }
      return {
        content: 'You are currently offline. Your message has been queued and will be sent when you reconnect.',
        suggestions: ['Check weather', 'My activities', 'Help'],
      };
    }

    // Primary path: call the agriagent-chat edge function
    try {
      const { data, error } = await edgeFunctions.agriAgentChat(this.userId ?? '', text, language, {
        history: this.conversationHistory.slice(-10).map(m => ({
          role: m.sender === 'user' ? 'user' : 'assistant',
          content: m.content,
        })),
      });

      if (!error && data) {
        agentAnalytics.trackMessage(false);
        const responseText = typeof data.response === 'object' && data.response !== null
          ? (data.response as { text: string }).text
          : data.response as unknown as string;
        return {
          content: agentPersonality.applyTone(responseText),
          suggestions: data.suggestions ?? ['Check weather', 'Log activity', 'Market prices'],
        };
      }
      console.warn('AgriAgent edge function error:', error?.message);
    } catch (err) {
      console.warn('AgriAgent edge function exception:', err);
    }

    // Fallback: cached responses
    const cachedResponse = cachedResponses.getResponse(text);
    if (cachedResponse) {
      agentAnalytics.trackMessage(false);
      return { ...cachedResponse, content: agentPersonality.applyTone(cachedResponse.content) };
    }

    // Last resort: default response
    const defaultResponse = cachedResponses.getDefaultResponse();
    agentAnalytics.trackMessage(false);
    return { ...defaultResponse, content: agentPersonality.applyTone(defaultResponse.content) };
  }

  async sendVoice(audioBlob: Blob): Promise<AgentResponse> {
    // For mock implementation, return a default response
    agentAnalytics.trackMessage(true, 'voice message');
    agentAnalytics.trackMessage(false);

    return {
      content: "I've received your voice message! Voice transcription will be available in a future update. For now, please use text to ask your question.",
      suggestions: ['Type your question', 'Help'],
    };
  }

  async getGreeting(): Promise<string> {
    const personalityGreeting = agentPersonality.getGreeting();
    const context = conversationContext.getSummary();
    
    return `${personalityGreeting}\n\n${context}`;
  }

  getSuggestions(context?: string): string[] {
    if (context) {
      const response = cachedResponses.getResponse(context);
      if (response?.suggestions) {
        return response.suggestions;
      }
    }

    return ['Check weather', 'Log activity', 'Market prices', 'Farming tips'];
  }

  getQuickActions(): QuickAction[] {
    return conversationActions.getQuickActions();
  }

  async processQueuedMessages(): Promise<void> {
    if (messageQueue.hasQueuedMessages()) {
      await messageQueue.processQueue(async (message) => {
        await this.sendMessage(message);
      });
    }
  }

  getQueuedMessageCount(): number {
    return messageQueue.getPendingCount();
  }

  getHistory(): ConversationMessage[] {
    return [...this.conversationHistory];
  }

  clearHistory(): void {
    this.conversationHistory = [];
    this.saveHistory();
    agentAnalytics.clearAnalytics();
  }

  addMessageToHistory(message: ConversationMessage): void {
    this.conversationHistory.push(message);
    this.saveHistory();
  }
}

export const agriAgentService = new AgriAgentService();
